import { MatrixPicker, withMatrix, type Quadrant } from '@/features/matrix'
import { BUSINESS_IMPACT_KEY, BUSINESS_IMPACT_MAX } from '@/features/cards/businessImpact'
import { Textarea } from '@/components/ui/textarea'
import { localTimeZone, zonedDateTimeToIso } from '@/lib/timezone'
import { teamIdsOf } from './cardTeams'
import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, Plus } from 'lucide-react'
import { apiGet, apiPost } from '@/api/axios-client'
import { getErrorMessage, isApiError } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { toDateInput } from '@/lib/dates'
import { useDraftAutosave } from '@/hooks/use-draft-autosave'
import { deleteLocalDraft, getLocalDraft, newDraftId, saveLocalDraft } from '@/features/drafts/draftStore'
import { DateTimeText } from '@/components/composites/date-time-text'
import { hasVisibleText, richTextToPlainText } from '@/lib/rich-text'
import { normalizeHumanText } from '@/lib/text-normalization'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RichTextEditor } from '@/components/composites/rich-text'
import { CARD_TYPE_LABEL, EFFORT_TITLE, PRIORITY_LABEL } from '@/components/composites/status-badge'
import { TagPicker } from '@/features/cards/TagPicker'
import type { Card, Delegation, CardTypeKey, Client, EffortKey, PriorityKey, TeamNode } from '@/types/entities'
import {
  DEFAULT_DUE_TIME,
  NONE,
  advisorsForTeam,
  approverClashMessage,
  useProjectOptions,
  useTeamOptions,
  type Person,
} from './pickerData'
import {
  ApproverSlots,
  DueDateTimeField,
  Hint,
  ProjectSelect,
  Required,
  TeamsSelect,
} from './pickers'
import { SearchSelect } from '@/components/composites/search-select'
import { VisibilityField } from './VisibilityField'
import { SourceLinksField, cleanSourceLinks } from '@/components/composites/source-links-field'
import { AttachmentsField, cleanAttachments, type AttachmentRef } from '@/components/composites/attachments-field'
import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ProjectDialog } from '@/features/projects/ProjectDialog'
import { AssigneePicker } from './AssigneePicker'
import { cardsApi } from '@/features/cards/api'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'

const PRIORITIES: PriorityKey[] = ['none', 'low', 'medium', 'high', 'urgent']
const KINDS: Array<{ key: CardTypeKey; hint: string }> = [
  { key: 'task', hint: 'Planned work, known outcome' },
  { key: 'bug', hint: 'Broken, or never worked' },
  { key: 'feature', hint: 'New capability' },
  { key: 'request', hint: 'An ask not yet agreed' },
  { key: 'question', hint: 'Needs an answer, not a build' },
]
const EVERYONE = 'all'

/** Module-level so the clock read is plainly an event-time call, not part of rendering. */
function fiveMinutesAgoIso(): string {
  return new Date(Date.now() - 5 * 60_000).toISOString()
}

function today(): string {
  return toDateInput(new Date())
}

function sameStringList(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

/** A trusted internal draft handed to the existing Work Item dialog by another Tapwork page. */
export interface WorkItemPrefill {
  title: string
  description: string
  cardType?: CardTypeKey
  /** Best-effort default. Project team restrictions still win once a project is chosen. */
  teamName?: string
  /** Source links supplied by trusted internal handoffs such as the Gmail Chrome extension. */
  sourceLinks?: string[]
}

interface WorkDraftPayload {
  title?: string
  description?: string
  teamIds?: string[]
  teamsTouched?: boolean
  ownerId?: string
  assigneeIds?: string[]
  projectId?: string
  dueDate?: string
  dueTime?: string
  timeZone?: string
  approver1Id?: string
  approver2Id?: string
  priority?: PriorityKey
  sourceLinks?: string[]
  attachments?: AttachmentRef[]
  autoArchive?: boolean
  effort?: EffortKey | ''
  clientContact?: string
  watcherIds?: string[]
  watcherTeamIds?: string[]
  kind?: CardTypeKey
  labelIds?: string[]
  visibleToUserIds?: string[]
  visibleToTeamIds?: string[]
}

/**
 * The Work Item v1 create form: the six required fields, plus Approver 1 and Approver 2.
 *
 * The engagement is chosen as a PROJECT, not as a delivery type and a client: the type and
 * the client are properties of the project, so the client is shown here as read-only text
 * rather than asked for a second time (and the API no longer accepts it).
 *
 * Stage and start date are not asked for — the backend sets To do and stamps the start date.
 * The target end date and time are locked once saved, and the form says so.
 *
 * Approvers are two named slots: Approver 1 is required, Approver 2 optional. The creator is
 * no longer auto-added when work is handed over — a required Approver 1 already guarantees
 * somebody signs it off, and adding a third the creator never chose is exactly what naming
 * the slots was meant to stop.
 */
/** Tomorrow in the browser's calendar, as YYYY-MM-DD — what most work is due. */
function tomorrowIso(now = new Date()): string {
  const d = new Date(now)
  d.setDate(d.getDate() + 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/** The project and first approver last used here, so the next item starts where the last one ended. */
function lastChoicesKey(userId: string, workspaceId: string): string {
  return `tapwork:new-work:last:${userId}:${workspaceId}`
}
function readLastChoices(userId: string, workspaceId: string): { projectId?: string; approver1Id?: string } {
  try {
    const raw = localStorage.getItem(lastChoicesKey(userId, workspaceId))
    return raw ? (JSON.parse(raw) as { projectId?: string; approver1Id?: string }) : {}
  } catch {
    return {}
  }
}

export function NewWorkItemDialog({
  boardId,
  workspaceId,
  members,
  clients,
  defaultOwnerId,
  parent,
  trigger,
  prefill,
  onPrefillConsumed,
  initialProjectId,
  initialClientId,
  creationContext,
}: {
  boardId: string
  workspaceId: string
  members: Person[]
  /** Only for naming the chosen project's client and listing its contacts. */
  clients: Client[]
  defaultOwnerId: string
  /** Creates a sub-item of this work item, pre-filled from it. */
  parent?: Card
  trigger?: ReactNode
  /** Opens the dialog immediately with fields seeded by another internal workflow. */
  prefill?: WorkItemPrefill
  /** Clears navigation state after the seeded dialog is closed or successfully submitted. */
  onPrefillConsumed?: () => void
  /** Project context supplied by a Project detail view. Form-only default; no record is changed until submit. */
  initialProjectId?: string
  /** Client context supplied by a Client detail view. Narrows the project picker to that client. */
  initialClientId?: string
  /** Optional source marker used only for Timeline-scoped audit history. */
  creationContext?: 'project_timeline'
}) {
  const queryClient = useQueryClient()
  const { userId: actorId } = useIdentity()
  const [routeParams, setParams] = useSearchParams()
  const draftParam = routeParams.get('draft')
  const listedDraft = draftParam && actorId ? getLocalDraft<WorkDraftPayload>(actorId, workspaceId, 'work', draftParam) : null
  const restored = listedDraft?.payload ?? null
  const [listedDraftId, setListedDraftId] = useState(() => listedDraft?.id ?? newDraftId())
  const [open, setOpen] = useState(Boolean(prefill || listedDraft))
  const [tried, setTried] = useState(false)
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
  const [checkingDuplicate, setCheckingDuplicate] = useState(false)
  const [creatingProject, setCreatingProject] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [step, setStep] = useState(0)
  const forceSubmit = useRef(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [visibleToUserIds, setVisibleToUserIds] = useState<string[]>(Array.isArray(restored?.visibleToUserIds) ? restored.visibleToUserIds : [])
  const [visibleToTeamIds, setVisibleToTeamIds] = useState<string[]>(Array.isArray(restored?.visibleToTeamIds) ? restored.visibleToTeamIds : [])
  // A sub-item starts as a copy of its parent's framing; the creator does the work by default.
  const last = !parent && !initialProjectId && actorId ? readLastChoices(actorId, workspaceId) : {}
  const initial = {
    teamIds: parent ? teamIdsOf(parent) : [],
    projectId: parent?.projectId ?? initialProjectId ?? last.projectId ?? '',
  }
  const defaultDueDate = useMemo(() => tomorrowIso(), [])
  const [title, setTitle] = useState(restored?.title ?? prefill?.title ?? '')
  const [description, setDescription] = useState(restored?.description ?? prefill?.description ?? '')
  const [businessImpact, setBusinessImpact] = useState('')
  const [matrix, setMatrix] = useState<Quadrant | null>(null)
  const [teamIds, setTeamIds] = useState<string[]>(Array.isArray(restored?.teamIds) ? restored.teamIds : initial.teamIds)
  /** Whether the person has opened the team picker. Until then the default above applies. */
  const [teamsTouched, setTeamsTouched] = useState(Boolean(restored?.teamsTouched))
  /** Owner: the one accountable assignee. */
  const [ownerId, setOwnerId] = useState(restored?.ownerId ?? defaultOwnerId)
  /** Everyone assigned, including the owner. */
  const [assigneeIds, setAssigneeIds] = useState<string[]>(Array.isArray(restored?.assigneeIds) ? restored.assigneeIds : (defaultOwnerId ? [defaultOwnerId] : []))
  const [projectId, setProjectId] = useState(restored?.projectId ?? initial.projectId)
  const [dueDate, setDueDate] = useState(restored?.dueDate ?? defaultDueDate)
  const [dueTime, setDueTime] = useState(restored?.dueTime ?? DEFAULT_DUE_TIME)
  const [timeZone, setTimeZone] = useState(restored?.timeZone ?? localTimeZone())
  const [approver1Id, setApprover1Id] = useState(restored?.approver1Id ?? last.approver1Id ?? '')
  const [approver2Id, setApprover2Id] = useState(restored?.approver2Id ?? NONE)
  const [priority, setPriority] = useState<PriorityKey>(restored?.priority ?? 'none')
  const [sourceLinks, setSourceLinks] = useState<string[]>(
    Array.isArray(restored?.sourceLinks)
      ? restored.sourceLinks
      : Array.isArray(prefill?.sourceLinks)
        ? prefill.sourceLinks
        : [],
  )
  const [attachments, setAttachments] = useState<AttachmentRef[]>(Array.isArray(restored?.attachments) ? restored.attachments : [])
  const [autoArchive, setAutoArchive] = useState(restored?.autoArchive !== false)
  const [effort, setEffort] = useState<EffortKey | ''>(restored?.effort ?? '')
  const [clientContact, setClientContact] = useState(restored?.clientContact ?? NONE)
  const [watchers, setWatchers] = useState<Set<string>>(() => new Set(Array.isArray(restored?.watcherIds) ? restored.watcherIds : []))
  const [watcherTeamIds, setWatcherTeamIds] = useState<Set<string>>(() =>
    new Set(Array.isArray(restored?.watcherTeamIds) ? restored.watcherTeamIds : []),
  )
  const [kind, setKind] = useState<CardTypeKey>(restored?.kind ?? prefill?.cardType ?? parent?.cardType ?? 'task')
  const [labelIds, setLabelIds] = useState<string[]>(Array.isArray(restored?.labelIds) ? restored.labelIds : (parent?.labelIds ?? []))
  const parentDue = parent?.dueAt ? parent.dueAt.slice(0, 10) : undefined

  const draftContext = parent?.id ?? initialProjectId ?? (initialClientId ? `client-${initialClientId}` : 'root')
  const draftStorageKey = `tapwork:draft:new-work:${actorId || 'anonymous'}:${workspaceId}:${boardId}:${draftContext}`
  const draftValue = {
    title, description, teamIds, teamsTouched, ownerId, assigneeIds, projectId, dueDate, dueTime,
    timeZone, approver1Id, approver2Id, priority, sourceLinks, attachments, autoArchive, effort,
    clientContact, watcherIds: [...watchers], watcherTeamIds: [...watcherTeamIds], kind, labelIds, visibleToUserIds, visibleToTeamIds,
  }
  const defaultAssigneeIds = defaultOwnerId ? [defaultOwnerId] : []
  const defaultKind = parent?.cardType ?? 'task'
  const defaultLabelIds = parent?.labelIds ?? []
  const draftDirty = !prefill && Boolean(
    title.trim() || hasVisibleText(description) || dueDate !== defaultDueDate || sourceLinks.some(Boolean) || attachments.length ||
    teamsTouched || !sameStringList(teamIds, initial.teamIds) || ownerId !== defaultOwnerId ||
    !sameStringList(assigneeIds, defaultAssigneeIds) || projectId !== initial.projectId ||
    dueTime !== DEFAULT_DUE_TIME || timeZone !== localTimeZone() || approver1Id !== (last.approver1Id ?? '') || approver2Id !== NONE ||
    priority !== 'none' || !autoArchive || effort || clientContact !== NONE || watchers.size > 0 || watcherTeamIds.size > 0 ||
    kind !== defaultKind || !sameStringList(labelIds, defaultLabelIds) || visibleToUserIds.length ||
    visibleToTeamIds.length,
  )
  const { lastSavedAt: draftSavedAt, saveNow: saveDraftNow, clearDraft } = useDraftAutosave({
    storageKey: draftStorageKey,
    enabled: open && !prefill && !listedDraft,
    dirty: draftDirty,
    value: draftValue,
    onRestore: (draft) => {
      setTitle(draft.title ?? '')
      setDescription(draft.description ?? '')
      setTeamIds(Array.isArray(draft.teamIds) ? draft.teamIds : initial.teamIds)
      setTeamsTouched(Boolean(draft.teamsTouched))
      setOwnerId(draft.ownerId ?? defaultOwnerId)
      setAssigneeIds(Array.isArray(draft.assigneeIds) ? draft.assigneeIds : (defaultOwnerId ? [defaultOwnerId] : []))
      setProjectId(draft.projectId ?? initial.projectId)
      setDueDate(draft.dueDate ?? '')
      setDueTime(draft.dueTime ?? DEFAULT_DUE_TIME)
      setTimeZone(draft.timeZone ?? localTimeZone())
      setApprover1Id(draft.approver1Id ?? '')
      setApprover2Id(draft.approver2Id ?? NONE)
      setPriority(draft.priority ?? 'none')
      setSourceLinks(Array.isArray(draft.sourceLinks) ? draft.sourceLinks : [])
      setAttachments(Array.isArray(draft.attachments) ? draft.attachments : [])
      setAutoArchive(draft.autoArchive !== false)
      setEffort(draft.effort ?? '')
      setClientContact(draft.clientContact ?? NONE)
      setWatchers(new Set(Array.isArray(draft.watcherIds) ? draft.watcherIds : []))
      setWatcherTeamIds(new Set(Array.isArray(draft.watcherTeamIds) ? draft.watcherTeamIds : []))
      setKind(draft.kind ?? parent?.cardType ?? 'task')
      setLabelIds(Array.isArray(draft.labelIds) ? draft.labelIds : (parent?.labelIds ?? []))
      setVisibleToUserIds(Array.isArray(draft.visibleToUserIds) ? draft.visibleToUserIds : [])
      setVisibleToTeamIds(Array.isArray(draft.visibleToTeamIds) ? draft.visibleToTeamIds : [])
    },
  })

  const { tree, teams: allTeams, isPending: teamsPending } = useTeamOptions(open)

  const { projects, isPending: projectsPending } = useProjectOptions(open)
  const availableProjects = initialClientId
    ? projects.filter((candidate) => candidate.clientId === initialClientId)
    : projects

  /**
   * A draft may suggest a team before the picker data exists (Support Tickets does this).
   * Keep that suggestion derived rather than setting state in an effect: once the person
   * touches the picker, their explicit choice wins; project team restrictions still apply.
   *
   * This is intentionally plain derived data rather than a memo. The React compiler can
   * safely optimize it itself, and the team list is small enough that the lookup is trivial.
   */
  let seededTeamIds = teamIds
  if (!teamsTouched && teamIds.length === 0 && prefill?.teamName) {
    const wanted = prefill.teamName.trim().toLowerCase()
    const match = allTeams.find((team) => {
      const leaf = team.label.split(' / ').at(-1)?.trim().toLowerCase()
      return leaf === wanted
    })
    if (match) seededTeamIds = [match.id]
  }

  /**
   * Teams offered as a one-click way to add several watchers at once. Flattened from the
   * tree because `teams` above is only id + label — watchers need the membership.
   */
  const teamWatchOptions = useMemo(() => {
    const out: Array<{ id: string; label: string; memberIds: string[] }> = []
    const walk = (nodes: TeamNode[], prefix: string) => {
      for (const node of nodes) {
        const label = prefix ? `${prefix} / ${node.name}` : node.name
        if (node.memberUserIds.length > 0) {
          out.push({ id: node.id, label, memberIds: node.memberUserIds })
        }
        walk(node.children, label)
      }
    }
    walk(tree, '')
    return out
  }, [tree])

  const resolvedWatcherIds = useMemo(() => {
    const next = new Set(watchers)
    for (const teamId of watcherTeamIds) {
      const team = teamWatchOptions.find((option) => option.id === teamId)
      for (const memberId of team?.memberIds ?? []) {
        if (memberId !== defaultOwnerId && memberId !== ownerId) next.add(memberId)
      }
    }
    return [...next]
  }, [defaultOwnerId, ownerId, teamWatchOptions, watcherTeamIds, watchers])

  const project = availableProjects.find((p) => p.id === projectId)

  /**
   * The chosen project decides who may be given the work.
   *
   * A project with no teams named constrains nothing — that is how every project created
   * before the field existed reads, and how a new one starts — so the full list stands in.
   */
  const projectTeamIds = project?.teamIds ?? null
  const teams =
    projectTeamIds && projectTeamIds.length > 0
      ? allTeams.filter((t) => projectTeamIds.includes(t.id))
      : allTeams
  /**
   * The teams this item will actually be filed under.
   *
   * Explicit choices always win. Before the picker is touched, a Team Lead defaults to the
   * team(s) they lead, narrowed by the selected project's allowed teams. Non-leads keep the
   * existing project-wide default after a project is selected. With no project and no lead
   * default, the field stays empty rather than pretending every team in the org was chosen.
   */
  const allowedTeamIds = new Set(teams.map((team) => team.id))
  const keptTeamIds = seededTeamIds.filter((id) => allowedTeamIds.has(id))
  // A Team Lead starts with the team(s) they actually lead when those teams are valid for the
  // selected project. This is only a default: opening the team picker makes the user's choice
  // authoritative, and the backend still validates the submitted team ids.
  const leadTeamIds = useMemo(() => {
    const ids: string[] = []
    const walk = (nodes: TeamNode[]) => {
      for (const node of nodes) {
        if (node.leadUserId === actorId) ids.push(node.id)
        walk(node.children)
      }
    }
    if (actorId) walk(tree)
    return ids
  }, [actorId, tree])
  const allowedLeadTeamIds = leadTeamIds.filter((id) => allowedTeamIds.has(id))
  // The teams the person belongs to, for the same kind of default when they lead none.
  const memberTeamIds = useMemo(() => {
    const ids: string[] = []
    const walk = (nodes: TeamNode[]) => {
      for (const node of nodes) {
        if (node.memberUserIds?.includes(actorId ?? '')) ids.push(node.id)
        walk(node.children)
      }
    }
    if (actorId) walk(tree)
    return ids
  }, [actorId, tree])
  const allowedMemberTeamIds = memberTeamIds.filter((id) => allowedTeamIds.has(id))
  /*
   * Defaults, in order: what was chosen; the teams you lead; then, for a project that names
   * its delivering teams, those teams. A project that names NO teams allows every team in the
   * org, so it must not select them all (that filed work under 13 teams nobody chose): it
   * defaults to the teams you belong to, or stays empty for you to pick.
   */
  const projectNamesTeams = Boolean(projectTeamIds && projectTeamIds.length > 0)
  const resolvedTeamIds =
    teamsTouched
      ? keptTeamIds
      : keptTeamIds.length > 0
        ? keptTeamIds
        : allowedLeadTeamIds.length > 0
          ? allowedLeadTeamIds
          : !projectId
            ? []
            : projectNamesTeams
              ? teams.map((team) => team.id)
              : allowedMemberTeamIds

  /** Filing your own work: the one case where nobody has to sign it off. */
  const selfAssigned = Boolean(ownerId && ownerId === defaultOwnerId)

  const projectClient = project?.clientId ? clients.find((c) => c.id === project.clientId) : undefined

  const active = useMemo(() => members.filter((m) => !m.disabled), [members])

  /**
   * Who this person may hand work to, and who may sign it off. Asked of the server rather
   * than worked out here — the rule lives in one place, and the backend re-checks on save.
   */
  const delegationQuery = useQuery({
    queryKey: qk.delegation(),
    queryFn: () => apiGet<Delegation>('members/delegation'),
    staleTime: 60_000,
    enabled: open,
  })
  const delegation = delegationQuery.data
  /** Both answers are needed before a picker can offer anything truthful. */
  const peopleReady = active.length > 0 && !delegationQuery.isPending
  const assignable = useMemo(
    () =>
      delegation
        ? active.filter((m) => delegation.canAssignTo.includes(m.id))
        : active.filter((m) => m.id === defaultOwnerId),
    [active, delegation, defaultOwnerId],
  )
  const approvable = useMemo(
    () =>
      delegation
        ? active.filter(
            (m) =>
              (delegation.approversFor[ownerId] ?? []).includes(m.id) && !assigneeIds.includes(m.id),
          )
        : [],
    [active, assigneeIds, delegation, ownerId],
  )
  const owner = active.find((m) => m.id === ownerId)

  const pointPersonApproverClash = approverClashMessage(
    ownerId,
    approver1Id,
    approver2Id === NONE ? null : approver2Id,
    owner?.displayName,
  )
  const assignedApproverId = assigneeIds.find(
    (id) => id !== ownerId && (id === approver1Id || id === approver2Id),
  )
  const approverClash =
    pointPersonApproverClash ??
    (assignedApproverId
      ? `${active.find((m) => m.id === assignedApproverId)?.displayName ?? 'An assigned person'} is also an approver. Someone doing the work cannot approve it.`
      : null)

  /**
   * Picking a team, from wherever the pick came from.
   *
   * The team's advisors are the natural approvers, so they seed the two slots — but only
   * while those slots are untouched, because a choice already made outranks a suggestion.
   */
  const chooseTeams = (ids: string[]) => {
    // From here on the selection is theirs, including an empty one.
    setTeamsTouched(true)
    setTeamIds(ids)
    // Advisors of the FIRST team seed the approver slots — the first is the primary
    // everywhere downstream, so it is the one whose advisors are the natural sign-off.
    const advisors = ids[0] ? advisorsForTeam(tree, ids[0], active) : []
    if (advisors[0] && !approver1Id) setApprover1Id(advisors[0])
    if (advisors[1] && approver2Id === NONE) setApprover2Id(advisors[1])
  }

  const reset = () => {
    setStep(0)
    setDetailOpen(false)
    setTried(false)
    setDuplicateOpen(false)
    setConfirmCloseOpen(false)
    setCheckingDuplicate(false)
    setTitle('')
    setDescription('')
    setTeamIds(initial.teamIds)
    setTeamsTouched(false)
    setOwnerId(defaultOwnerId)
    setAssigneeIds(defaultOwnerId ? [defaultOwnerId] : [])
    setProjectId(initial.projectId)
    setDueDate(tomorrowIso())
    setDueTime(DEFAULT_DUE_TIME)
    setApprover1Id('')
    setApprover2Id(NONE)
    setPriority('none')
    setKind(parent?.cardType ?? 'task')
    setSourceLinks([])
    setAttachments([])
    setAutoArchive(true)
    setEffort('')
    setClientContact(NONE)
    setWatchers(new Set())
    setWatcherTeamIds(new Set())
    setMoreOpen(false)
    setLabelIds(parent?.labelIds ?? [])
    setVisibleToUserIds([])
    setVisibleToTeamIds([])
  }

  const create = useMutation({
    mutationFn: (options?: { confirmRecentDuplicate?: boolean }) =>
      apiPost<Card>('cards', {
        visibleToUserIds,
        visibleToTeamIds,
        boardId,
        confirmRecentDuplicate: options?.confirmRecentDuplicate ?? false,
        ...(creationContext ? { creationContext } : {}),
        title: title.trim(),
        // HTML now. The backend sanitizes it and stamps `descriptionFormat` itself — that
        // field is returned, never sent.
        // Optional on the form: when left empty the title stands in, so nothing is filed blank.
        description: hasVisibleText(description) ? description : `<p>${escapeHtml(title.trim())}</p>`,
        // Why it matters to the business — lets the person doing it weigh it against everything else.
        customAttrs: withMatrix(businessImpact.trim() ? { [BUSINESS_IMPACT_KEY]: businessImpact.trim() } : {}, matrix ? { urgent: matrix.urgent, important: matrix.important } : null),
        teamIds: resolvedTeamIds,
        ownerId,
        assigneeIds,
        // The client and the engagement type ride on the project; the API refuses both here.
        projectId,
        // The exact instant that was chosen, in the creator's own timezone.
        // Read in the chosen zone, not the browser's. `??` never fires — the form refuses
        // to submit without both — and keeps the type honest rather than asserting.
        dueAt: zonedDateTimeToIso(dueDate, dueTime, timeZone) ?? new Date().toISOString(),
        // Two named slots. `approverIds` is a 400 now — the request schema is strict.
        // Omitted entirely when filing your own work with nobody named — the API reads an
        // absent field as "no approver", and a self-assigned item may then go straight to
        // Completed by the person who did it.
        ...(approver1Id ? { approver1Id } : {}),
        approver2Id: approver2Id === NONE ? null : approver2Id,
        parentId: parent?.id ?? null,
        priority,
        cardType: kind,
        // The first still fills the old single column while both exist; the rest only
        // ever lived in the list.
        sourceLink: cleanSourceLinks(sourceLinks)[0] ?? null,
        sourceLinks: cleanSourceLinks(sourceLinks),
        attachments: cleanAttachments(attachments),
        autoArchive,
        effort: effort || null,
        clientContact: !projectClient || clientContact === NONE ? null : clientContact,
        watcherIds: resolvedWatcherIds,
        labelIds,
      }),
    onSuccess: (card) => {
      toast.success(`Created “${card.title}”`)
      void queryClient.invalidateQueries({ queryKey: qk.cardsPrefix(boardId) })
      void queryClient.invalidateQueries({ queryKey: qk.board(boardId) })
      if (creationContext === 'project_timeline') {
        void queryClient.invalidateQueries({ queryKey: ['project', card.projectId, 'timeline'] })
      }
      if (actorId && !parent) {
        try {
          localStorage.setItem(lastChoicesKey(actorId, workspaceId), JSON.stringify({ projectId: card.projectId ?? undefined, approver1Id: approver1Id || undefined }))
        } catch {
          /* remembering is a convenience */
        }
      }
      // Lets the Tapwork extension close the popup window it opened for this item.
      window.postMessage({ type: 'TAPWORK_WORK_ITEM_CREATED' }, window.location.origin)
      clearDraft()
      if (actorId) deleteLocalDraft({ id: listedDraftId, kind: 'work', userId: actorId, workspaceId })
      setOpen(false)
      reset()
      onPrefillConsumed?.()
      // Show what was just made: open its drawer, and if the board is filtered to a person
      // who is not the assignee, follow the item rather than let it vanish behind the filter.
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const person = next.get('person') || defaultOwnerId
          if (person !== EVERYONE && card.ownerId && person !== card.ownerId) {
            next.set('person', card.ownerId)
          }
          next.delete('draft')
          next.set('card', card.id)
          return next
        },
        { replace: false },
      )
    },
    onError: (error) => {
      // The local check makes this immediate; the server repeats it so two tabs/devices cannot
      // race past the warning.
      if (isApiError(error) && error.code === 'RECENT_DUPLICATE_ACTION') setDuplicateOpen(true)
    },
    // Other failures stay inline beside the submit button; the backend's message names the
    // rule that was broken.
  })

  const submitWithDuplicateCheck = async () => {
    setCheckingDuplicate(true)
    try {
      const recent = await cardsApi.query({
        filter: {
          workspaceId,
          boardId,
          createdByIds: actorId ? [actorId] : undefined,
          createdAfter: fiveMinutesAgoIso(),
          archived: false,
        },
        limit: 50,
      })
      const wantedTitle = normalizeHumanText(title)
      const wantedDescription = normalizeHumanText(richTextToPlainText(description))
      const duplicate = recent.items.some((item) => {
        // Duplicate warnings are only about a still-active action. Archived or deleted work
        // is historical context, not something the user is accidentally doing twice now.
        if (item.archived || item.deletedAt) return false
        const existingDescription = item.descriptionFormat === 'html'
          ? richTextToPlainText(item.description)
          : item.description
        return normalizeHumanText(item.title) === wantedTitle &&
          normalizeHumanText(existingDescription) === wantedDescription
      })
      if (duplicate) {
        setDuplicateOpen(true)
        return
      }
    } catch {
      // Courtesy guard only: a temporary lookup failure must not block legitimate creation.
    } finally {
      setCheckingDuplicate(false)
    }
    create.mutate(undefined)
  }

  const missing = {
    title: !title.trim(),
    // Not `!description.trim()`: a cleared editor still emits `<p></p>`, which is a
    // non-empty string that renders as nothing. Same question the backend asks on write.
    teamIds: resolvedTeamIds.length === 0,
    ownerId: !ownerId,
    projectId: !projectId,
    dueDate: !dueDate,
    dueTime: !dueTime,
    /*
     * Required, EXCEPT when somebody is filing their own work.
     *
     * Sign-off exists so that work is not marked done by the person who did it. That has
     * nothing to say about a task you raised for yourself and will finish yourself: there is
     * no second party whose interest an approver protects, and demanding one means either a
     * colleague rubber-stamping noise or the work never being filed at all. The API applies
     * the identical rule; this is so the form does not refuse what the server would accept.
     */
    approver1Id: !approver1Id && !selfAssigned,
  }
  const complete = !Object.values(missing).some(Boolean)
  const flag = (key: keyof typeof missing) => tried && missing[key]
  const STEP_FIELDS: Array<Array<keyof typeof missing>> = [['title'], ['projectId', 'teamIds', 'ownerId', 'approver1Id'], ['dueDate', 'dueTime']]
  const STEP_NAMES = ['What', 'Who & where', 'When']
  const LAST_STEP = STEP_FIELDS.length - 1
  const stepOk = (index: number) => !(STEP_FIELDS[index] ?? []).some((key) => missing[key])
  const goNext = () => {
    if (!stepOk(step)) {
      setTried(true)
      return
    }
    setTried(false)
    setStep((current) => Math.min(LAST_STEP, current + 1))
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setOpen(true)
          return
        }
        if (draftDirty) {
          saveDraftNow()
          setConfirmCloseOpen(true)
          return
        }
        setOpen(false)
        reset()
        onPrefillConsumed?.()
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm">
            <Plus className="size-3.5" />
            New work item
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-xl">
        <form
          className="flex min-h-0 flex-col"
          onKeyDown={(e) => {
            // Ctrl/Cmd + Enter files it from anywhere in the form, including the editor.
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              forceSubmit.current = true
              e.currentTarget.requestSubmit()
            }
          }}
          onSubmit={(e) => {
            e.preventDefault()
            const force = forceSubmit.current
            forceSubmit.current = false
            if (step < LAST_STEP && !force) {
              goNext()
              return
            }
            setTried(true)
            if (!complete) {
              const firstBad = STEP_FIELDS.findIndex((_, index) => !stepOk(index))
              if (firstBad >= 0) setStep(firstBad)
              return
            }
            void submitWithDuplicateCheck()
          }}
        >
          <DialogHeader className="gap-1 border-b border-border px-5 py-3">
            <DialogTitle className="text-base">{parent ? 'New sub-item' : 'New work item'}</DialogTitle>
            <DialogDescription className="text-xs">
              {parent
                ? `Part of “${parent.title}”. Team and project are copied from it; change them if needed.`
                : `Step ${step + 1} of ${LAST_STEP + 1} · ${STEP_NAMES[step]}. Takes about a minute.`}
            </DialogDescription>
            <div className="mt-1 flex gap-1" aria-hidden>
              {STEP_NAMES.map((name, index) => (
                <span key={name} className={cn('h-1 flex-1 rounded-full', index <= step ? 'bg-brand' : 'bg-border')} />
              ))}
            </div>
          </DialogHeader>

          <div className="grid gap-4 overflow-y-auto px-5 py-4">
            {/* What */}
            <section className={cn('grid gap-4', step !== 0 && 'hidden')}>
              <div className="grid gap-1.5">
                <Label htmlFor="wi-title">
                  Title <Required />
                </Label>
                <Input
                  id="wi-title"
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What needs to happen?"
                  maxLength={300}
                  aria-invalid={flag('title')}
                />
                <Hint show={flag('title')}>Give it a title.</Hint>
              </div>

              <div className="grid gap-1.5">
                <Label>Type</Label>
                <div role="radiogroup" aria-label="Type" className="flex flex-wrap gap-1.5">
                  {KINDS.map((item) => {
                    const on = kind === item.key
                    return (
                      <button
                        key={item.key}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        title={item.hint}
                        onClick={() => {
                          setKind(item.key)
                          if (item.key === 'bug' && priority === 'none') setPriority('high')
                        }}
                        className={cn(
                          'h-8 rounded-full border px-3 text-xs font-medium transition-colors dur-fast',
                          on ? 'border-brand bg-brand-soft text-brand-dark' : 'border-border hover:bg-muted',
                        )}
                      >
                        {CARD_TYPE_LABEL[item.key]}
                      </button>
                    )
                  })}
                </div>
              </div>

              <Collapsible open={detailOpen || hasVisibleText(description) || Boolean(businessImpact)} onOpenChange={setDetailOpen}>
                <CollapsibleTrigger asChild>
                  <button type="button" className="text-xs font-medium text-brand hover:underline">
                    {detailOpen || hasVisibleText(description) || businessImpact ? 'Detail' : '+ Add detail (optional)'}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3 grid gap-4">
              <div className="grid gap-1.5">
                <Label>
                  Description <span className="font-normal text-text-muted">(optional — the title is used if empty)</span>
                </Label>
                <RichTextEditor
                  ariaLabel="Description"
                  value={description}
                  onChange={setDescription}
                  mentionPeople={active}
                  placeholder="Enough detail for the assignee to start without asking."
                />
                <p className="text-tiny text-text-muted">Use @Full Name to mention a Tapwork member.</p>
              </div>

              <div className="grid gap-1.5">
                <Label>Business impact</Label>
                <Textarea
                  aria-label="Business impact"
                  value={businessImpact}
                  maxLength={BUSINESS_IMPACT_MAX}
                  rows={2}
                  onChange={(event) => setBusinessImpact(event.target.value)}
                  placeholder="What happens to the business if this slips? Who or what does it unblock?"
                />
                <p className="text-tiny text-text-muted">Helps whoever picks this up decide what to do first.</p>
              </div>
                </CollapsibleContent>
              </Collapsible>
            </section>

            {/* The fields people need on almost every Work Item stay visible. */}
            <section className={cn('grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-12', step !== 1 && 'hidden')}>
              <div className="grid min-w-0 gap-1.5 sm:col-span-5">
                <Label>
                  Team <Required />
                </Label>
                <TeamsSelect
                  value={resolvedTeamIds}
                  teams={teams}
                  pending={teamsPending}
                  emptyHint="This project names no teams that you can pick from."
                  invalid={flag('teamIds')}
                  showChips={false}
                  onValueChange={chooseTeams}
                />
                <Hint show={flag('teamIds')}>Every item belongs to at least one team.</Hint>
              </div>

              <div className="grid min-w-0 gap-1.5 sm:col-span-7">
                <Label>
                  Assigned people <Required />
                </Label>
                <AssigneePicker
                  selectedIds={assigneeIds}
                  pointPersonId={ownerId}
                  people={assignable}
                  meId={defaultOwnerId}
                  invalid={flag('ownerId')}
                  disabled={!peopleReady}
                  onChange={(nextAssignees, nextPointPerson) => {
                    setAssigneeIds(nextAssignees)
                    setOwnerId(nextPointPerson)
                  }}
                />
                {peopleReady ? (
                  <p className="text-tiny text-text-muted">
                    {assignable.length <= 1
                      ? 'You can put work on your own plate. Ask the relevant Team Lead to assign someone else.'
                      : 'Up to 10 people. The checked person is the owner.'}
                  </p>
                ) : null}
              </div>

              <div className="grid min-w-0 gap-1.5 sm:col-span-7">
                <Label>
                  Project <Required />
                </Label>
                <ProjectSelect
                  value={projectId}
                  projects={availableProjects}
                  clients={clients}
                  pending={projectsPending}
                  invalid={flag('projectId')}
                  onValueChange={(v) => {
                    setProjectId(v)
                    setClientContact(NONE)
                  }}
                  onCreate={() => setCreatingProject(true)}
                />
                <Hint show={flag('projectId')}>Every work item belongs to a project.</Hint>
              </div>

              <div className="grid min-w-0 gap-1.5 sm:col-span-5">
                <Label>Client</Label>
                <p className="flex min-h-9 items-center text-sm">
                  {!project ? (
                    initialClientId ? (clients.find((candidate) => candidate.id === initialClientId)?.name ?? 'Selected client') : <span className="text-text-muted">Pick a project first</span>
                  ) : projectClient ? (
                    projectClient.name
                  ) : (
                    <span className="text-text-muted">
                      {project.type === 'internal' ? 'Internal — no client' : 'No client'}
                    </span>
                  )}
                </p>
                {project ? <p className="text-tiny text-text-muted">Inherited from {project.name}.</p> : null}
              </div>

            </section>

            {/* Approval is part of the core assignment, so keep it visible instead of hiding it in More options. */}
            <div className={cn(step !== 1 && 'hidden')}>
            <ApproverSlots
              idPrefix="wi"
              approver1Id={approver1Id}
              approver2Id={approver2Id}
              onApprover1={setApprover1Id}
              onApprover2={setApprover2Id}
              people={approvable}
              invalid1={flag('approver1Id')}
              required1={!selfAssigned}
              hint={
                selfAssigned
                  ? 'Approver 1 is optional when you are filing your own work. Add one if you still want a separate sign-off before completion.'
                  : 'Approver 1 is required because this work is assigned to someone else. Approver 2 is optional.'
              }
              clash={approverClash}
            />
            </div>

            <section className={cn('grid gap-4', step !== 2 && 'hidden')}>
              <div className="grid gap-1.5">
                <Label>
                  Is it urgent? Is it important? <span className="font-normal text-text-muted">(optional — pick a box, or leave it blank)</span>
                </Label>
                <MatrixPicker value={matrix} onChange={setMatrix} />
              </div>
              <div className="grid gap-1.5 sm:col-span-12">
                <Label htmlFor="wi-due-date">
                  Target end date &amp; time <Required />
                </Label>
                <DueDateTimeField
                  idPrefix="wi-due"
                  date={dueDate}
                  time={dueTime}
                  onDate={setDueDate}
                  onTime={setDueTime}
                  timeZone={timeZone}
                  onTimeZone={setTimeZone}
                  min={today()}
                  max={parentDue}
                  invalid={flag('dueDate') || flag('dueTime')}
                  note={
                    !flag('dueDate') && !flag('dueTime') ? (
                      <p className="text-tiny text-text-muted">
                        It becomes overdue the moment this passes.
                        {parentDue ? ` No later than the parent’s ${parentDue}.` : ''}
                      </p>
                    ) : null
                  }
                />
                <Hint show={flag('dueDate') || flag('dueTime')}>
                  When exactly should this be done by?
                </Hint>
              </div>

              <div className="grid gap-1.5">
                <Label>About how long will it take?</Label>
                <div role="radiogroup" aria-label="Approximate effort" className="grid grid-cols-3 gap-1.5">
                  {([['s', 'Quick', '< 2 hrs'], ['m', 'Half day', 'few hrs'], ['l', 'Day+', 'big one']] as Array<[EffortKey, string, string]>).map(([e, label, hint]) => {
                    const on = effort === e
                    return (
                      <button
                        key={e}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        title={EFFORT_TITLE[e]}
                        onClick={() => setEffort(on ? '' : e)}
                        className={cn(
                          'rounded-md border px-2 py-1.5 text-left transition-colors dur-fast',
                          on ? 'border-brand bg-brand-soft text-brand-dark' : 'border-border hover:bg-muted',
                        )}
                      >
                        <span className="block text-xs font-medium">{label}</span>
                        <span className="block text-tiny text-text-muted">{hint}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label>How important?</Label>
                <div role="radiogroup" aria-label="Priority" className="flex flex-wrap gap-1.5">
                  {PRIORITIES.map((item) => {
                    const on = priority === item
                    return (
                      <button
                        key={item}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setPriority(item)}
                        className={cn(
                          'h-8 rounded-full border px-3 text-xs font-medium transition-colors dur-fast',
                          on ? 'border-brand bg-brand-soft text-brand-dark' : 'border-border hover:bg-muted',
                        )}
                      >
                        {PRIORITY_LABEL[item]}
                      </button>
                    )
                  })}
                </div>
              </div>
            </section>

            <Collapsible open={moreOpen} onOpenChange={setMoreOpen} className={cn('rounded-lg border border-border-soft', step !== 2 && 'hidden')}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium hover:bg-muted"
                >
                  <span>
                    More options
                    <span className="ml-2 font-normal text-text-muted">visibility, files, tags, watchers &amp; administration</span>
                  </span>
                  <ChevronDown className={cn('size-4 shrink-0 transition-transform', moreOpen && 'rotate-180')} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="grid gap-5 border-t border-border-soft px-4 py-4">
              <div className="grid gap-1.5 sm:col-span-2 rounded-lg border border-border-soft p-3">
                <div>
                  <p className="text-sm font-medium">Visibility</p>
                  <p className="text-tiny text-text-muted">
                    {visibleToUserIds.length === 0 && visibleToTeamIds.length === 0
                      ? 'Everyone with normal Work access can see this item.'
                      : `Restricted to ${visibleToUserIds.length} person${visibleToUserIds.length === 1 ? '' : 's'} and ${visibleToTeamIds.length} team${visibleToTeamIds.length === 1 ? '' : 's'}.`}
                  </p>
                </div>
                <VisibilityField
                  userIds={visibleToUserIds}
                  teamIds={visibleToTeamIds}
                  onChange={({ userIds, teamIds }) => { setVisibleToUserIds(userIds); setVisibleToTeamIds(teamIds) }}
                  members={active}
                  teams={tree}
                />
              </div>
                  <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {projectClient ? (
                      <div className="grid gap-1.5">
                        <Label>Client contact</Label>
                        <Select value={clientContact} onValueChange={setClientContact}>
                          <SelectTrigger><SelectValue placeholder="Who asked?" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>Nobody in particular</SelectItem>
                            {projectClient.contacts.map((ct) => (
                              <SelectItem key={ct.name} value={ct.name}>
                                {ct.name}
                                {ct.role ? <span className="ml-1 text-text-muted">· {ct.role}</span> : null}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null}

                    <div className="grid gap-1.5">
                      <Label>Tags</Label>
                      <div className="flex min-h-9 items-center">
                        <TagPicker workspaceId={workspaceId} selected={labelIds} onChange={setLabelIds} />
                      </div>
                    </div>
                  </section>

                  <SourceLinksField
                    value={sourceLinks}
                    onChange={setSourceLinks}
                    hint="The Slack thread, email, ticket or transcript this came from."
                  />

                  <AttachmentsField onChange={setAttachments} />

                  <section className="grid gap-4 rounded-lg bg-surface-sunken p-4">
                    <div>
                      <p className="text-sm font-medium">Watchers</p>
                      <p className="mt-0.5 text-tiny text-text-muted">
                        Watchers are notified about updates; they do not change who is assigned.
                      </p>
                    </div>

                    <div className="grid gap-1.5">
                      <Label>Watchers</Label>
                      <SearchSelect
                        multiple
                        value={[
                          ...watchers,
                          ...[...watcherTeamIds].map((id) => `team:${id}`),
                        ]}
                        onChange={(ids) => {
                          const nextTeams = ids
                            .filter((id) => id.startsWith('team:'))
                            .map((id) => id.slice('team:'.length))
                          const nextPeople = ids.filter((id) => !id.startsWith('team:'))
                          setWatcherTeamIds(new Set(nextTeams))
                          setWatchers(new Set(nextPeople))
                        }}
                        options={[
                          ...active
                            .filter((m) => m.id !== defaultOwnerId && m.id !== ownerId)
                            .map((m) => ({ id: m.id, label: m.displayName, photoUrl: m.photoUrl, group: 'People' })),
                          ...teamWatchOptions.map((team) => ({
                            id: `team:${team.id}`,
                            label: team.label,
                            hint: `${team.memberIds.length} member${team.memberIds.length === 1 ? '' : 's'}`,
                            group: 'Teams',
                          })),
                        ]}
                        placeholder="Search people or teams to notify…"
                        emptyHint="Nobody else to add."
                      />
                      <p className="text-tiny text-text-muted">
                        Selecting a team adds its current members as watchers. The creator is always notified automatically.
                      </p>
                    </div>

                  </section>

                  <label className="flex items-start gap-2 rounded-md border border-border-soft px-3 py-2">
                    <Checkbox
                      checked={!autoArchive}
                      onCheckedChange={(next) => setAutoArchive(next !== true)}
                      className="mt-0.5"
                      aria-label="Keep this out of the archive"
                    />
                    <span className="grid gap-0.5">
                      <span className="text-xs font-medium">Keep this on the board</span>
                      <span className="text-tiny text-text-muted">
                        Completed work normally moves to the archive after two weeks. Keep long-lived references on the board.
                      </span>
                    </span>
                  </label>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <DialogFooter className="border-t border-border px-5 py-3">
            {tried && !complete ? (
              <p className="mr-auto self-center text-tiny text-danger">Fill in the required fields.</p>
            ) : create.error ? (
              // The backend's own words. It refuses things this form cannot know about — an
              // approver who is also the assignee, a due date past the parent's — and
              // paraphrasing those into "Something went wrong" throws away the fix.
              <p className="mr-auto self-center text-tiny text-danger" role="alert">
                {getErrorMessage(create.error)}
              </p>
            ) : null}
            {draftSavedAt && !prefill ? (
              <span className="mr-auto self-center text-tiny text-text-muted">
                Draft saved <DateTimeText value={draftSavedAt} />
              </span>
            ) : null}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (draftDirty) {
                  saveDraftNow()
                  setConfirmCloseOpen(true)
                } else {
                  setOpen(false)
                  reset()
                  onPrefillConsumed?.()
                }
              }}
            >
              Cancel
            </Button>
            {step > 0 ? (
              <Button type="button" variant="ghost" onClick={() => { setTried(false); setStep(step - 1) }}>
                Back
              </Button>
            ) : null}
            {step < LAST_STEP ? (
              <Button type="button" onClick={goNext}>
                Next
              </Button>
            ) : (
              <Button type="submit" disabled={create.isPending || checkingDuplicate}>
                {create.isPending || checkingDuplicate ? 'Checking…' : 'Create work item'}
              </Button>
            )}
          </DialogFooter>
        </form>

        {/*
          Over the form, not instead of it: a required field whose answer does not exist yet
          should not cost somebody everything they have typed. The project dialog can open a
          client the same way, so New work -> New project -> New client is one unbroken path,
          and each step returns with its answer already filled in.
        */}
        <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Close this form?</AlertDialogTitle>
              <AlertDialogDescription>
                Your draft is saved in this browser. Keep it for the next time you open this form,
                or discard it permanently.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep editing</AlertDialogCancel>
              <Button
                variant="outline"
                onClick={() => {
                  saveDraftNow()
                  if (actorId) {
                    saveLocalDraft({
                      id: listedDraftId,
                      kind: 'work',
                      userId: actorId,
                      workspaceId,
                      title: title.trim() || 'Untitled work item',
                      updatedAt: new Date().toISOString(),
                      payload: draftValue,
                    })
                  }
                  clearDraft()
                  setConfirmCloseOpen(false)
                  setOpen(false)
                  setParams((prev) => { const next = new URLSearchParams(prev); next.delete('draft'); return next }, { replace: true })
                  reset()
                  setListedDraftId(newDraftId())
                  onPrefillConsumed?.()
                }}
              >
                Keep draft &amp; close
              </Button>
              <AlertDialogAction
                onClick={() => {
                  clearDraft()
                  if (actorId) deleteLocalDraft({ id: listedDraftId, kind: 'work', userId: actorId, workspaceId })
                  setConfirmCloseOpen(false)
                  setOpen(false)
                  setParams((prev) => { const next = new URLSearchParams(prev); next.delete('draft'); return next }, { replace: true })
                  reset()
                  onPrefillConsumed?.()
                }}
              >
                Discard draft
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>You have just created an identical Work Item.</AlertDialogTitle>
              <AlertDialogDescription>Are you sure you want to do it again?</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>No</AlertDialogCancel>
              <AlertDialogAction onClick={() => { setDuplicateOpen(false); create.mutate({ confirmRecentDuplicate: true }) }}>Yes</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ProjectDialog
          clients={clients}
          members={members}
          open={creatingProject}
          onOpenChange={setCreatingProject}
          onCreated={(created) => {
            setProjectId(created.id)
            setClientContact(NONE)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
